import { Component, type ErrorInfo, type ReactNode, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/shared/components/ui/Button';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

function ErrorFallback({ error, errorInfo }: { error: Error | null; errorInfo: ErrorInfo | null }) {
  const [showDetails, setShowDetails] = useState(false);
  const isDev = import.meta.env.DEV;

  return (
    <div className={cn('flex min-h-screen items-center justify-center bg-background p-4')}>
      <div className={cn('w-full max-w-lg space-y-4 text-center')}>
        <h1 className="text-2xl font-bold text-foreground">문제가 발생했습니다</h1>
        <p className="text-muted-foreground">Something went wrong</p>

        <Button variant="default" onClick={() => window.location.reload()} className="mt-4">
          새로고침 / Reload
        </Button>

        {isDev && error && (
          <div className="mt-6 text-left">
            <Button variant="outline" size="sm" onClick={() => setShowDetails((prev) => !prev)}>
              {showDetails ? 'Hide Details' : 'Show Details'}
            </Button>

            {showDetails && (
              <div className="mt-2 space-y-2">
                <pre className="overflow-auto rounded border bg-muted p-3 text-xs text-destructive">
                  {error.message}
                  {'\n\n'}
                  {error.stack}
                </pre>
                {errorInfo?.componentStack && (
                  <pre className="overflow-auto rounded border bg-muted p-3 text-xs text-muted-foreground">
                    {errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  override render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} errorInfo={this.state.errorInfo} />;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
